/**
 * Printer Settings — pair / unpair / test the Bluetooth thermal
 * printer, and choose paper width. (Iter 81)
 *
 * Route: /settings/printer   (admin panel)
 *
 * Uses the shared `thermalPrinter.js` util so the same paired
 * device works everywhere in the app.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Printer, Bluetooth, XCircle, CheckCircle, Warning, TestTube } from "@phosphor-icons/react";
import {
  pairPrinter, disconnectPrinter, testPrint,
  getPaperWidth, setPaperWidth, getLastDeviceName,
  whyBluetoothUnavailable,
} from "../../lib/thermalPrinter";

export default function PrinterSettings() {
  const [paper, setPaper] = useState(getPaperWidth());
  const [device, setDevice] = useState(getLastDeviceName());
  const [busy, setBusy] = useState(false);
  const why = whyBluetoothUnavailable();

  const onPair = async () => {
    if (why) { toast.error(why); return; }
    setBusy(true);
    try {
      const d = await pairPrinter();
      setDevice(d.name);
      toast.success(`Paired · ${d.name}`);
    } catch (e) { toast.error(e?.message || "Pairing failed"); }
    finally { setBusy(false); }
  };
  const onUnpair = () => {
    disconnectPrinter();
    localStorage.removeItem("rescigo.thermal.last_device_name");
    setDevice("");
    toast.success("Printer forgotten");
  };
  const onTest = async () => {
    if (why) { toast.error(why); return; }
    setBusy(true);
    const t = toast.loading("Sending test page…");
    try {
      await testPrint();
      toast.success("Test page sent", { id: t });
    } catch (e) {
      if (e?.message === "NEEDS_PAIR") {
        try {
          await pairPrinter();
          await testPrint();
          setDevice(getLastDeviceName());
          toast.success("Test page sent", { id: t });
        } catch (e2) { toast.error(e2?.message || "Failed", { id: t }); }
      } else { toast.error(e?.message || "Failed", { id: t }); }
    } finally { setBusy(false); }
  };
  const onPaper = (mm) => { setPaperWidth(mm); setPaper(mm); };

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6" data-testid="printer-settings">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">
          Devices
        </div>
        <h1 className="font-heading text-2xl md:text-3xl text-[#1A1A1A]">
          Thermal printer
        </h1>
        <p className="text-sm text-[#5C5A55] mt-1 max-w-xl">
          Pair a Bluetooth thermal printer once per device — every "Print" button in Rescigo
          (POS, Delivery, Service Requests, Invoices) will then send receipts straight over
          Bluetooth. Works on Chrome / Edge desktop and Chrome on Android.
        </p>
      </div>

      {why && (
        <div className="rounded-lg border border-[#F4C7B9] bg-[#F9EDE7] p-4 text-sm text-[#7A2E18] flex items-start gap-2" data-testid="printer-unsupported">
          <Warning size={18} weight="fill" className="mt-0.5 shrink-0" />
          <div><strong>Bluetooth not available:</strong> {why}</div>
        </div>
      )}

      {/* Device card */}
      <div className="bg-white border border-[#E5E2D9] rounded-lg p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-[#D4E0D1] flex items-center justify-center shrink-0">
            <Printer size={22} color="#2D5A27" weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">
              Paired printer
            </div>
            <div className="font-heading text-lg" data-testid="printer-device-name">
              {device || "No printer paired"}
            </div>
            {device ? (
              <div className="text-[11px] text-[#2D5A27] mt-1 inline-flex items-center gap-1">
                <CheckCircle size={11} weight="fill" />
                Ready · reconnects automatically on print
              </div>
            ) : (
              <div className="text-[11px] text-[#8A8780] mt-1">
                Pair once — the browser will remember this device for next time.
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={onPair} disabled={busy || !!why}
                  data-testid="printer-pair-btn"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#2D5A27] text-white text-sm font-medium hover:bg-[#1F3F1A] disabled:opacity-60">
            <Bluetooth size={14} weight="fill" />
            {device ? "Pair another printer" : "Pair printer"}
          </button>
          <button onClick={onTest} disabled={busy || !!why}
                  data-testid="printer-test-btn"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#E5E2D9] text-sm text-[#2D5A27] hover:bg-[#F2F0E9] disabled:opacity-60">
            <TestTube size={14} weight="fill" />
            Test print
          </button>
          {device && (
            <button onClick={onUnpair}
                    data-testid="printer-forget-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#E5E2D9] text-sm text-[#7A2E18] hover:bg-[#F9EDE7]">
              <XCircle size={14} weight="fill" />
              Forget printer
            </button>
          )}
        </div>
      </div>

      {/* Paper width */}
      <div className="bg-white border border-[#E5E2D9] rounded-lg p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">
          Paper width
        </div>
        <div className="font-heading text-lg mt-0.5">Roll size</div>
        <p className="text-xs text-[#5C5A55] mt-1">
          Most hand-held printers (PSF-588, POS-58, etc.) use 58 mm rolls. Counter-top printers use 80 mm.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-3 max-w-md">
          <button onClick={() => onPaper(58)} data-testid="printer-paper-58"
                  className={`h-16 rounded-md border-2 text-sm font-heading flex flex-col items-center justify-center transition-colors ${
                    paper === 58 ? "border-[#2D5A27] bg-[#D4E0D1]" : "border-[#E5E2D9] hover:border-[#B7B3A6]"
                  }`}>
            <span className="text-base">58 mm</span>
            <span className="text-[10px] text-[#5C5A55] font-normal">32 chars/line · default</span>
          </button>
          <button onClick={() => onPaper(80)} data-testid="printer-paper-80"
                  className={`h-16 rounded-md border-2 text-sm font-heading flex flex-col items-center justify-center transition-colors ${
                    paper === 80 ? "border-[#2D5A27] bg-[#D4E0D1]" : "border-[#E5E2D9] hover:border-[#B7B3A6]"
                  }`}>
            <span className="text-base">80 mm</span>
            <span className="text-[10px] text-[#5C5A55] font-normal">48 chars/line</span>
          </button>
        </div>
      </div>

      {/* Help card */}
      <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded-lg p-5 text-xs text-[#5C5A55] leading-relaxed">
        <div className="font-heading text-sm text-[#1A1A1A] mb-2">
          Pairing tips (PSF-588 &amp; similar)
        </div>
        <ol className="list-decimal pl-4 space-y-1">
          <li>Switch the printer on and make sure the blue LED is blinking.</li>
          <li>If your phone's OS asks for a PIN, use <code>0000</code> or <code>1234</code>. Rescigo talks to the printer over BLE, so the OS pairing dialog is only a first-time formality.</li>
          <li>Click <em>Pair printer</em> above and pick the entry that starts with <code>PSF</code>, <code>POS58</code>, <code>MTP</code>, <code>Zjiang</code> or your model name.</li>
          <li>Hit <em>Test print</em>. If nothing prints, forget the device and try again — some cheap printers need one warm-up connection.</li>
          <li>Only one browser tab at a time can hold the Bluetooth handle. Close other tabs printing from the same printer.</li>
        </ol>
      </div>
    </div>
  );
}
